import { getSupportedDevicesSync } from "../main/supportedDevices";
import { canonicalKeyboardTypeForRhythmPolicy } from "./globalRhythmKeyboardPolicy";
import {
  is24GWorkMode,
  isHuafenda24GInterfaceRhythmTarget,
  resolveEquipmentWorkMode,
} from "./hfdWirelessMusicRhythm";
import { matchHuafendaEquipmentToConfigRow } from "./supportedKeyboardMatch";
import { normalizeEquipmentName } from "./supportedKeyboardMatch";

/** 华奋达 2.4G 接收器 WebHID PID（X87=0xfefe，X87 V2 / Neon / X75GC=0xfef9，X75-Pro 等=0xfefa） */
export const HFD_24G_DONGLE_PRODUCT_IDS = [0xfefe, 0xfef9, 0xfefa] as const;

export function isHfd24GDongleProductId(pid: number): boolean {
  return (HFD_24G_DONGLE_PRODUCT_IDS as readonly number[]).includes(pid);
}

function hidProductNameOf(hidDevice: unknown): string {
  const pn = (hidDevice as { productName?: unknown } | undefined)?.productName;
  return typeof pn === "string" ? pn.trim().toLowerCase() : "";
}

/**
 * NK20 2.4G 接收器（WebHID 如 eweadn-nk20 2.4g）。
 */
export function isNk20ReceiverHid(hidDevice: unknown): boolean {
  if (!hidDevice || typeof hidDevice !== "object") return false;
  if (Number((hidDevice as { vendorId?: unknown }).vendorId) !== 0x0c45) {
    return false;
  }
  return hidProductNameOf(hidDevice).includes("nk20");
}

/** WebHID 是否为华奋达 2.4G 接收器（含 X87 V2 / Neon / X75GC 的 0xfef9） */
export function isHuafendaDongleWebHid(hidDevice: unknown): boolean {
  if (isNk20ReceiverHid(hidDevice)) return false;
  if (!hidDevice || typeof hidDevice !== "object") return false;
  const vid = Number((hidDevice as { vendorId?: unknown }).vendorId);
  const pid = Number((hidDevice as { productId?: unknown }).productId);
  if (vid !== 0x0c45) return false;
  if (isHfd24GDongleProductId(pid)) return true;
  return /2\.4g|dongle/.test(hidProductNameOf(hidDevice));
}

type HidLike = {
  vendorId?: number;
  productId?: number;
  productName?: string;
  collections?: Array<{ usagePage?: number }>;
};

type ConfigRow = Record<string, unknown>;

function usagePagesOf(hid: HidLike): Set<number> {
  const out = new Set<number>();
  for (const c of hid.collections ?? []) {
    const up = Number(c?.usagePage);
    if (Number.isFinite(up)) out.add(up);
  }
  return out;
}

/** 与首页 connect 一致：2.4G 用 adaptation24G / adaptation1，有线用 adaptation */
export function huafendaConfigRowMatchesHidCollections(
  configRow: ConfigRow,
  hid: HidLike,
  on24G: boolean
): boolean {
  const pages = usagePagesOf(hid);
  if (pages.size === 0) return false;

  const lists: unknown[] = on24G
    ? [configRow.adaptation24G, configRow.adaptation1, configRow.adaptation]
    : [configRow.adaptation];

  for (const raw of lists) {
    if (!Array.isArray(raw)) continue;
    for (const up of raw) {
      if (pages.has(Number(up))) return true;
    }
  }
  return false;
}

function isHuafenda24GConfigRow(row: ConfigRow): boolean {
  return row.is4G === true || row.workMode === 2 || row.workMode === "2";
}

function relevantHuafendaConfigRows(
  equipment: Record<string, unknown>,
  on24G: boolean
): ConfigRow[] {
  const vid = Number(equipment.vendorId);
  const eqPid = Number(equipment.productId);
  const kt = canonicalKeyboardTypeForRhythmPolicy(equipment.keyboardType);
  const dn = normalizeEquipmentName(
    equipment.displayname ?? equipment.displayName ?? equipment.productName
  );

  const rows = (getSupportedDevicesSync().keyboard as ConfigRow[]).filter(
    (row) => String(row.solutionRovider ?? "") === "huafenda"
  );

  const byProductLine = rows.filter((row) => {
    if (Number(row.vendorId) !== vid) return false;
    const rowKt = canonicalKeyboardTypeForRhythmPolicy(row.keyboardType);
    if (kt && rowKt && kt !== rowKt) return false;
    const rowDn = normalizeEquipmentName(
      row.displayname ?? row.displayName ?? row.productName
    );
    if (dn && rowDn && dn !== rowDn) return false;
    return on24G ? isHuafenda24GConfigRow(row) : !isHuafenda24GConfigRow(row);
  });

  if (byProductLine.length > 0) return byProductLine;

  if (Number.isFinite(eqPid)) {
    const byPid = rows.filter((row) => Number(row.productId) === eqPid);
    if (byPid.length > 0) return byPid;
  }

  const matched = rows.find((row) =>
    matchHuafendaEquipmentToConfigRow(equipment, row)
  );
  if (matched) return [matched];

  return [];
}

/**
 * 为 setDevicesHuafenda / SDK 重连解析应 connect 的 HID 列表（已按优先级排序）。
 */
export function resolveHuafendaConnectHidCandidates(
  equipment: Record<string, unknown>,
  authorizedHids: unknown[]
): HidLike[] {
  const vid = Number(equipment.vendorId);
  if (!Number.isFinite(vid)) return [];

  const on24G =
    is24GWorkMode(resolveEquipmentWorkMode(equipment)) ||
    isHuafenda24GInterfaceRhythmTarget(equipment);
  const eqPid = Number(equipment.productId);
  const configRows = relevantHuafendaConfigRows(equipment, on24G);
  const hids = (authorizedHids as HidLike[]).filter(
    (h) => Number(h?.vendorId) === vid
  );

  const scored: Array<{ hid: HidLike; score: number }> = [];

  for (const hid of hids) {
    const hidPid = Number(hid.productId);
    let score = 0;

    if (on24G) {
      if (!isHuafendaDongleWebHid(hid)) {
        if (!Number.isFinite(eqPid) || hidPid !== eqPid) continue;
      } else {
        score += 10;
      }
      if (Number.isFinite(eqPid) && hidPid === eqPid) score += 20;

      let usageMatched = false;
      for (const cfg of configRows) {
        if (Number(cfg.productId) === hidPid) score += 5;
        if (huafendaConfigRowMatchesHidCollections(cfg, hid, true)) {
          score += 15;
          usageMatched = true;
        }
      }
      if (!usageMatched && !isHuafendaDongleWebHid(hid)) continue;
      if (isHuafendaDongleWebHid(hid) && configRows.length === 0) score += 5;
    } else {
      if (Number.isFinite(eqPid) && hidPid !== eqPid) continue;
      if (isHuafendaDongleWebHid(hid)) continue;

      let usageMatched = false;
      for (const cfg of configRows) {
        if (huafendaConfigRowMatchesHidCollections(cfg, hid, false)) {
          score += 15;
          usageMatched = true;
        }
      }
      if (!usageMatched && configRows.length > 0) continue;
      score += 10;
    }

    scored.push({ hid, score });
  }

  scored.sort((a, b) => b.score - a.score);
  // 只按对象引用去重：两枚 0xfefe「2.4G Dongle」vid/pid/name 完全相同，
  // 若再按字符串 key 折叠，后连的接收器会把先连的挤掉，子应用永远绑不到正确 HID。
  const seen = new Set<HidLike>();
  const out: HidLike[] = [];
  for (const { hid } of scored) {
    if (seen.has(hid)) continue;
    seen.add(hid);
    out.push(hid);
  }
  return out;
}
